/* ALPAKA theme: motion layer, header and menus, cart drawer, fast buy box, sticky bar,
   email letter, group calculator. Vanilla, no dependencies. Content works without JS. */
(() => {
  const root = document.documentElement;
  root.classList.add('js');
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const motion = root.classList.contains('motion') && !reduce;
  const routes = Object.assign({ cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update' }, window.routes || {});
  const money = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100).replace(/\.00$/, '');
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const store = {
    get(k) { try { return JSON.parse(localStorage.getItem(k)) || {}; } catch { return {}; } },
    set(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } },
  };
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];

  /* 1. Reveal: CSS scroll-driven where supported, IntersectionObserver otherwise. */
  // Photos open and line art draws once, when it arrives on screen (never scrubbed by scrolling,
  // so nothing is left half-drawn at the end of a page).
  const targets = $$('.reveal, .reveal-media, .draw');
  if (!motion || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-in'));
  } else {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    targets.forEach((el) => io.observe(el));
    // anything already above the fold or passed by a jump link draws immediately
    addEventListener('load', () => targets.forEach((el) => { if (el.getBoundingClientRect().top < innerHeight) el.classList.add('is-in'); }));
    // safety net: line art never stays half-drawn, even if an observer callback is missed (owner feedback #5, #34)
    addEventListener('load', () => setTimeout(() => $$('.draw').forEach((el) => el.classList.add('is-in')), 4000));
  }

  /* 2. Header: hairline once scrolled; hides after a deliberate scroll down (12px), returns only on a scroll up or
        near the top. On the way down with the sticky buy bar showing, the header stays hidden (QA round 2: forcing it
        back when the bar appeared made it jump during one continuous scroll). The FAQ chip bar follows its offset. */
  const header = $('[data-header]');
  if (header) {
    let last = scrollY;
    let anchor = scrollY;
    let ticking = false;
    addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = scrollY;
        header.classList.toggle('is-scrolled', y > 8);
        const busy = document.body.classList.contains('drawer-open') || header.contains(document.activeElement) && $('.site-nav__item.is-open', header);
        if (!motion || busy || y < 320) { header.classList.remove('is-hidden'); anchor = y; }
        else if ((y > last) !== (last > anchor)) anchor = last; // direction changed: measure from here
        if (motion && !busy && y >= 320 && Math.abs(y - anchor) > 12) header.classList.toggle('is-hidden', y > anchor);
        document.body.classList.toggle('header-hidden', header.classList.contains('is-hidden'));
        last = y;
        ticking = false;
      });
    }, { passive: true });
  }

  /* 3. Desktop dropdown panels (hover via CSS; click/keyboard via the chevron button). */
  const navItems = $$('.site-nav__item[data-has-panel]');
  const closeNav = (except) => navItems.forEach((li) => {
    if (li === except) return;
    li.classList.remove('is-open');
    $('.site-nav__toggle', li)?.setAttribute('aria-expanded', 'false');
  });
  navItems.forEach((li) => {
    const btn = $('.site-nav__toggle', li);
    btn?.addEventListener('click', () => {
      const open = !li.classList.contains('is-open');
      closeNav(li);
      li.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', String(open));
    });
    li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget)) { li.classList.remove('is-open'); btn?.setAttribute('aria-expanded', 'false'); } });
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('.site-nav__item')) closeNav(); });

  /* 4. Dialogs: mobile menu and cart drawer (native <dialog>: focus trap and Esc for free). */
  const openModal = (dlg, opener) => {
    if (!dlg || dlg.open) return;
    dlg._opener = opener || document.activeElement;
    dlg.showModal();
    document.body.classList.add('drawer-open');
  };
  $$('dialog[data-modal]').forEach((dlg) => {
    dlg.addEventListener('close', () => {
      document.body.classList.remove('drawer-open');
      dlg._opener?.focus?.({ preventScroll: true });
    });
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('[data-close]')) dlg.close(); });
  });
  const menu = $('#MenuDrawer');
  $$('[data-menu-open]').forEach((b) => b.addEventListener('click', () => openModal(menu, b)));
  menu?.addEventListener('click', (e) => { if (e.target.closest('a[href]')) menu.close(); });

  /* 5. Cart: fetch, render the drawer, change lines, note. */
  const drawer = $('#CartDrawer');
  const fetchCart = () => fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  const lineHTML = (item, i) => {
    const img = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=240` : '';
    // a pre-order line carries its ship-by date in the variant title (K15): "Batch 02 · pre-order · ships by Dec 15"
    const pre = /pre-order/i.test(item.variant_title || '');
    const unit = !pre && item.variant_title && item.variant_title !== 'Default Title' ? ` · ${esc(item.variant_title)}` : '';
    const preLine = pre ? `<p class="cart-line__pre">${$('#icon-calendar')?.innerHTML || ''}<span>${esc(item.variant_title)}</span></p>` : '';
    return `<div class="cart-line">
      <a class="frame" href="${esc(item.url)}" tabindex="-1" aria-hidden="true">${img ? `<img src="${esc(img)}" alt="" width="80" height="100" decoding="async">` : ''}</a>
      <div>
        <a class="cart-line__title" href="${esc(item.url)}">${esc(drawer?.dataset.productTitle || String(item.product_title).replace(/^ALPAKA\s+/, ''))}</a>
        ${preLine}<p class="cart-line__meta">${money(item.final_price)} each${unit}</p>
        <div class="qty qty--sm" data-line="${i + 1}">
          <button type="button" data-qty-change="-1" aria-label="Remove one">${$('#icon-minus')?.innerHTML || '−'}</button>
          <input type="number" min="0" value="${item.quantity}" aria-label="Quantity of ${esc(item.product_title)}" data-line-qty>
          <button type="button" data-qty-change="1" aria-label="Add one">${$('#icon-plus')?.innerHTML || '+'}</button>
        </div>
      </div>
      <div><div class="cart-line__price">${money(item.final_line_price)}</div><button type="button" class="cart-line__remove" data-remove="${i + 1}">Remove</button></div>
    </div>`;
  };
  const renderCart = (cart) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
    if (!drawer) return cart;
    const list = $('[data-cart-items]', drawer);
    const foot = $('[data-cart-foot]', drawer);
    const empty = $('[data-cart-empty]', drawer);
    list.innerHTML = cart.items.map(lineHTML).join('');
    empty.hidden = cart.item_count > 0;
    list.hidden = foot.hidden = cart.item_count === 0;
    // a gift note written on the product page shows here, so there is one note, not two
    const note = $('[data-cart-note]', drawer);
    if (note && document.activeElement !== note) { note.value = cart.note || ''; if (cart.note) note.closest('details')?.setAttribute('open', ''); }
    const tip = $('[data-cart-tip]', drawer);
    if (tip) tip.hidden = cart.item_count === 0;
    $('[data-cart-total]', drawer).textContent = money(cart.total_price);
    const disc = $('[data-cart-discounts]', drawer);
    if (disc) disc.textContent = cart.total_discount ? `Includes ${money(cart.total_discount)} off` : '';
    const prog = $('[data-cart-progress]', drawer);
    if (prog) {
      const from = parseInt(prog.dataset.freeFrom, 10) || 0;
      const need = from - cart.item_count;
      prog.hidden = !from || cart.item_count === 0;
      $('[data-progress-more]', prog).hidden = need <= 0;
      $('[data-progress-done]', prog).hidden = need > 0;
      if (need > 0) $('[data-progress-text]', prog).textContent = `Add ${need} more ${need === 1 ? 'block' : 'blocks'} and shipping is on us.`;
      $('[data-progress-bar]', prog)?.style.setProperty('--p', `${from ? Math.min(100, (cart.item_count / from) * 100) : 100}%`);
    }
    return cart;
  };
  const openCart = (opener) => fetchCart().then(renderCart).then(() => openModal(drawer, opener));
  // For Shopify's agent cart tools (layout/theme.liquid): open the drawer, or re-render it after an outside change.
  window.alpakaCart = { open: () => openCart(), refresh: () => fetchCart().then(renderCart) };
  const changeLine = (line, quantity) => fetch(`${routes.cart_change_url}.js`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }),
  }).then((r) => r.json()).then(renderCart);
  const addToCart = (body) => fetch(`${routes.cart_add_url}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body })
    .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart.'); return d; });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-cart-open]');
    if (opener && drawer) { e.preventDefault(); openCart(opener); return; }
    const more = e.target.closest('[data-progress-add]');
    if (more) {
      const from = parseInt(more.closest('[data-cart-progress]')?.dataset.freeFrom, 10) || 2;
      fetchCart().then((cart) => { const i = cart.items.findIndex((it) => it.quantity < from); return i < 0 ? cart : changeLine(i + 1, from); });
      return;
    }
    const rm = e.target.closest('[data-remove]');
    if (rm) { e.preventDefault(); changeLine(+rm.dataset.remove, 0).then(() => { if (rm.hasAttribute('data-reload')) location.reload(); }); }
    const step = e.target.closest('[data-qty-change]');
    if (step) {
      const wrap = step.closest('.qty');
      const input = $('input', wrap);
      const min = wrap.dataset.line ? 0 : (parseInt(input.min, 10) || 1);
      const max = parseInt(input.max, 10);
      let next = (parseInt(input.value, 10) || 0) + parseInt(step.dataset.qtyChange, 10);
      next = Math.max(min, Number.isFinite(max) ? Math.min(next, max) : next);
      input.value = next;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      if (wrap.dataset.line) changeLine(+wrap.dataset.line, next).then(() => { if (wrap.hasAttribute('data-reload')) location.reload(); });
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) changeLine(+input.closest('.qty').dataset.line, Math.max(0, parseInt(input.value, 10) || 0)).then(() => { if (input.closest('[data-reload]')) location.reload(); });
    const note = e.target.closest('[data-cart-note]');
    if (note) fetch(`${routes.cart_update_url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note.value }) });
  });

  /* 6. The seal is pressed into the button the moment it is tapped (the request runs underneath). */
  const SEAL = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9.1"/><circle cx="12" cy="12" r="5.9"/><path d="M12 8.4C13.1 9.7 13.3 12.7 12 15.6C10.7 12.7 10.9 9.7 12 8.4Z"/></svg>';
  const stamp = (button, text) => {
    const label = $('[data-label]', button) || button;
    if (!label.dataset.was) label.dataset.was = label.textContent;
    if (motion) {
      const s = document.createElement('span');
      s.className = 'press-seal';
      s.innerHTML = SEAL;
      button.append(s);
      setTimeout(() => s.remove(), 1700);
    }
    label.textContent = text;
  };
  const unstamp = (button) => {
    const label = $('[data-label]', button) || button;
    if (label.dataset.was) { label.textContent = label.dataset.was; delete label.dataset.was; }
    $$('.press-seal', button).forEach((s) => s.remove());
  };
  const press = (button, count) => {
    try { sessionStorage.setItem('alpaka-added', '1'); } catch { /* private mode */ }
    if (!button) return;
    const label = $('[data-label]', button) || button;
    const was = label.dataset.was || label.textContent;
    label.textContent = 'Added';
    setTimeout(() => { label.textContent = was; delete label.dataset.was; }, 1600);
    $$('[data-cart-count]').forEach((el) => {
      if (count != null) { el.textContent = count; el.dataset.count = count; }
      el.classList.remove('is-bump');
      void el.offsetWidth;
      el.classList.add('is-bump');
    });
  };
  window.alpakaPress = press;

  // Generic AJAX add-to-cart forms (article call-outs, menu card).
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-add-to-cart]');
    if (!form) return;
    e.preventDefault();
    const btn = e.submitter || $('[type="submit"]', form);
    btn.disabled = true;
    stamp(btn, 'Adding…');
    const t0 = Date.now();
    addToCart(new FormData(form)).then(fetchCart).then((cart) => {
      renderCart(cart); press(btn, cart.item_count);
      setTimeout(() => openModal(drawer, btn), motion ? Math.max(0, 650 - (Date.now() - t0)) : 0);
    }).catch((err) => { unstamp(btn); const m = $('[data-form-message]', form); if (m) m.textContent = err.message; })
      .finally(() => { btn.disabled = false; });
  });

  /* 7. Buy box: quantity cards (theme settings > Offer) or "a different number", the total on Buy now, Buy now straight
        to checkout with exactly this quantity (cart permalink: pressing it twice or an older cart never adds extra
        blocks), Add to cart into the drawer with the seal press. ?blocks=N preselects a card (ritual pages, emails). */
  const boxes = $$('[data-buy-box]');
  const wanted = parseInt(new URLSearchParams(location.search).get('blocks'), 10);
  boxes.forEach((box) => {
    const form = $('form[data-buy-form]', box);
    if (!form) return;
    const price = parseInt(box.dataset.price, 10) || 0;
    const ship = parseInt(box.dataset.ship, 10) || 0;
    const freeFrom = parseInt(box.dataset.freeFrom, 10) || 0;
    const withShip = box.dataset.withShip === 'true';
    const max = parseInt(box.dataset.max, 10) || 99;
    const radios = $$('input[name="quantity"][type="radio"]', box);
    const other = $('[data-other]', box);
    const qty = $('[data-buy-qty]', box);
    const total = $('[data-buy-total]', box);
    const detail = $('[data-tier-detail]', box);
    const msg = $('[data-form-message]', box);
    const now = $('[data-buy-now]', box);
    const addOnly = $('[data-add-only]', box);
    const clamp = (n) => Math.min(max, Math.max(1, parseInt(n, 10) || 1));
    const setLabel = (btn, text) => { const l = $('[data-label]', btn) || btn; l.textContent = text; };
    const chosen = () => radios.find((r) => r.checked);
    const tierFor = (q) => radios.find((r) => parseInt(r.value, 10) === q);
    const quantity = () => (other?.open || !radios.length ? clamp(qty?.value) : clamp(chosen()?.value));
    const totalFor = (q, r) => {
      if (r) return withShip ? parseInt(r.dataset.total, 10) : parseInt(r.dataset.line, 10);
      const line = q * price;
      return withShip && (!freeFrom || q < freeFrom) ? line + ship : line;
    };
    const summary = () => {
      const q = quantity();
      const r = other?.open ? tierFor(q) : chosen();
      const t = totalFor(q, r);
      const free = !freeFrom ? false : q >= freeFrom;
      const line = r ? parseInt(r.dataset.line, 10) : q * price;
      return { q, t, r, line, ship: free ? 0 : ship, label: r?.dataset.label || `${q} ${q === 1 ? 'block' : 'blocks'}`, free };
    };
    const update = () => {
      // "A different number": the card with that quantity lights up; any other number is spelled out under the cards.
      if (other?.open) { const q = quantity(); radios.forEach((r) => { r.checked = parseInt(r.value, 10) === q; }); }
      else if (radios.length && !chosen()) radios[0].checked = true;
      const s = summary();
      if (total) total.textContent = money(s.t).replace(/\.00$/, '');
      if (detail) {
        let d = (s.r && s.r.dataset.detail) || '';
        if (!s.r && other?.open) {
          const ships = !freeFrom ? '' : s.free ? ' · ships free' : ` · + ${money(ship).replace(/\.00$/, '')} shipping`;
          d = `${s.label} · ${money(s.q * price).replace(/\.00$/, '')}${ships}`;
        }
        detail.textContent = d; detail.hidden = !d;
      }
      box.dispatchEvent(new CustomEvent('alpaka:buy-change', { bubbles: true, detail: s }));
    };
    if (wanted > 0) {
      const match = tierFor(wanted);
      if (match) match.checked = true;
      else if (other && qty && wanted <= max) { other.open = true; qty.value = wanted; }
      // K10 stepper (no cards, no "different number"): ?blocks=N sets the stepper, so Buy now shows N blocks
      else if (!radios.length && qty) qty.value = clamp(wanted);
    }
    radios.forEach((r) => r.addEventListener('change', () => { if (other) other.open = false; update(); }));
    other?.addEventListener('toggle', () => {
      if (other.open && qty) qty.value = clamp(chosen()?.value || qty.value);
      update();
    });
    qty?.addEventListener('input', update);
    qty?.addEventListener('change', () => { qty.value = clamp(qty.value); update(); });
    update();
    // Back from checkout (bfcache or form restore): the price on Buy now follows the card that is actually checked.
    addEventListener('pageshow', () => { update(); if (now) { now.disabled = false; setLabel(now, now.dataset.labelText || 'Buy now'); } });
    // A gift note already in the cart (Buy now, then Back) is shown again in the open panel, so it is never sent unseen.
    const giftBox = $('[data-gift]', box);
    const giftField = $('[data-gift-note]', box);
    let cartGift = false;
    const syncGift = () => fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).then((c) => {
      const note = (c.note || '').trim();
      cartGift = note.startsWith('Gift note:');
      if (cartGift && giftField && !giftField.value.trim()) { giftField.value = note.replace(/^Gift note:\s*/, ''); if (giftBox) giftBox.open = true; }
    }).catch(() => {});
    if (giftField) addEventListener('pageshow', syncGift);
    addEventListener('load', update);
    queueMicrotask(update); // once the sticky bar below listens
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!now || now.disabled) return;
      const q = quantity();
      now.disabled = true;
      setLabel(now, 'Going to checkout…');
      if (msg) msg.textContent = '';
      const id = $('[name="id"]', form)?.value;
      if (!id) { now.disabled = false; setLabel(now, now.dataset.labelText || 'Buy now'); return; }
      // cart permalink: exactly this quantity; ?ref= keeps the campaign, ?note= carries a gift note as the order note
      const qs = new URLSearchParams();
      const ref = new URLSearchParams(location.search).get('ref');
      if (ref) qs.set('ref', ref);
      const gift = $('[data-gift-note]', box)?.value.trim();
      if (gift) qs.set('note', `Gift note: ${gift}`);
      const go = () => { window.location.href = `/cart/${encodeURIComponent(id)}:${q}${qs.toString() ? `?${qs}` : ''}`; };
      // the reader emptied the gift note: the old one in the cart must not travel with this order
      if (!gift && cartGift) {
        fetch(`${routes.cart_update_url}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: '' }) })
          .catch(() => {}).finally(go);
      } else go();
    });
    addOnly?.addEventListener('click', () => {
      if (addOnly.classList.contains('is-busy')) return;
      addOnly.classList.add('is-busy');
      addOnly.setAttribute('aria-busy', 'true');
      if (msg) msg.textContent = '';
      stamp(addOnly, 'Adding…');
      const t0 = Date.now();
      const body = new FormData();
      body.append('id', $('[name="id"]', form)?.value || box.dataset.variant);
      body.append('quantity', String(quantity()));
      const gift = $('[data-gift-note]', box)?.value.trim();
      const withNote = (r) => (gift ? fetch(`${routes.cart_update_url}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: `Gift note: ${gift}` }),
      }).then(() => r) : r);
      addToCart(body).then(withNote).then(fetchCart).then((cart) => {
        renderCart(cart);
        press(addOnly, cart.item_count);
        setTimeout(() => openModal(drawer, addOnly), motion ? Math.max(0, 650 - (Date.now() - t0)) : 0);
      }).catch((err) => { unstamp(addOnly); if (msg) msg.textContent = err.message; })
        .finally(() => { addOnly.classList.remove('is-busy'); addOnly.removeAttribute('aria-busy'); });
    });
  });

  /* 8. Sticky buy bar (phones): visible whenever the in-page Buy now is out of view, above or below.
        On the home page it also waits until the hero has scrolled away (the hero has its own button). */
  const bar = $('[data-sticky-buy]');
  const mainBox = boxes[0];
  const setBar = (show) => {
    if (!bar || bar.classList.contains('is-visible') === show) return;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', String(!show));
    document.body.classList.toggle('sticky-buy-on', show);
  };
  if (bar) document.body.classList.add('has-sticky-buy');
  if (bar && mainBox && !$('[data-buy-now]', mainBox)) {
    // waitlist state: nothing to buy, so no buy bar
    bar.remove();
  } else if (bar && mainBox) {
    const target = $('[data-buy-now]', mainBox) || mainBox;
    const isHome = document.body.classList.contains('template-index');
    let out = false;
    let footerIn = false; // QA v3-6: the bar steps aside once the footer (seal, horizon) comes into view
    const decide = () => setBar(out && !footerIn && (!isHome || scrollY > innerHeight * 0.6));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => { out = !e.isIntersecting; decide(); }).observe(target);
      const foot = $('.site-footer');
      if (foot) new IntersectionObserver(([e]) => { footerIn = e.isIntersecting; decide(); }).observe(foot);
    } else {
      const r = () => { const b = target.getBoundingClientRect(); out = b.bottom < 0 || b.top > innerHeight; decide(); };
      addEventListener('scroll', r, { passive: true }); r();
    }
    if (isHome) addEventListener('scroll', decide, { passive: true });
    const stickyBtn = $('[data-sticky-now]', bar);
    stickyBtn?.addEventListener('click', () => { $('[data-buy-now]', mainBox)?.click(); });
    // pre-order (K15): the bar says so, with the ship-by date, exactly like the buy box
    const pre = mainBox.dataset.state === 'pre';
    if (pre && stickyBtn) stickyBtn.textContent = 'Pre-order';
    // pre-order: the title line carries the ship-by date, so the bar stays two short lines on a phone
    const stickyTitle = $('[data-sticky-title]', bar);
    if (pre && stickyTitle && mainBox.dataset.ships) { stickyTitle.textContent = `Ships by ${mainBox.dataset.ships}`; stickyTitle.classList.add('is-pre'); }
    const sum = $('[data-sticky-summary]', bar);
    // "1 block · $40" / "2 blocks · $80": the same price as the headline and the cart; no offer copy (Kate K10)
    const m = (c) => money(c).replace(/\.00$/, '');
    const sync = (d) => { if (sum && d) sum.textContent = `${d.label} · ${m(d.line)}`; };
    mainBox.addEventListener('alpaka:buy-change', (e) => sync(e.detail));
  } else if (bar) {
    // No buy box on this page: the bar links to the product page instead.
    addEventListener('scroll', () => setBar(scrollY > innerHeight * 0.9), { passive: true });
  }

  /* 8b. Silent loops: autoplay, muted, loop, playsinline; they loop forever while on screen and pause off screen to
         save battery. With reduced motion (or if the browser blocks autoplay) the poster stays. */
  const loops = $$('video[data-loop]');
  if (loops.length) {
    if (!motion || !('IntersectionObserver' in window)) {
      loops.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
    } else {
      const vio = new IntersectionObserver((entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.loop = true; e.target.play().catch(() => {}); } else e.target.pause();
      }), { threshold: 0.15 });
      loops.forEach((v) => { v.muted = true; v.loop = true; v.addEventListener('ended', () => { v.currentTime = 0; v.play().catch(() => {}); }); vio.observe(v); });
    }
  }

  /* 9. Gallery: seed dots follow the swipe; dots scroll to their photo. */
  $$('[data-gallery]').forEach((g) => {
    const dots = $$('.gallery-dots button', g.parentElement);
    if (!dots.length) return;
    const items = [...g.children];
    let t;
    g.addEventListener('scroll', () => {
      cancelAnimationFrame(t);
      t = requestAnimationFrame(() => {
        const w = items[0].getBoundingClientRect().width + 12;
        const i = Math.min(items.length - 1, Math.round(g.scrollLeft / w));
        dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
      });
    }, { passive: true });
    dots.forEach((d, k) => d.addEventListener('click', () => g.scrollTo({ left: items[k].offsetLeft - items[0].offsetLeft, behavior: motion ? 'smooth' : 'auto' })));
  });

  /* 10. Table of contents: mark the section being read. */
  const toc = $('[data-toc]');
  if (toc && 'IntersectionObserver' in window) {
    const links = $$('a[href^="#"]', toc);
    const map = new Map(links.map((a) => [decodeURIComponent(a.hash.slice(1)), a]));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        links.forEach((a) => a.removeAttribute('aria-current'));
        map.get(e.target.id)?.setAttribute('aria-current', 'true');
      });
    }, { rootMargin: '-20% 0px -70% 0px' });
    map.forEach((_, id) => { const el = document.getElementById(id); if (el) io.observe(el); });
    toc.addEventListener('click', (e) => { if (e.target.closest('a')) $('details', toc)?.removeAttribute('open'); });
  }

  /* 10b. Facts & FAQ topics: the chip of the topic being read is marked (aria-current) and, on phones, scrolled into
          view in the chip bar. */
  const faqNav = $('.faq-nav');
  if (faqNav) {
    /* QA v3-9: the active topic is the last group whose heading has crossed a line just under the sticky chip bar
       (not a band in mid-screen, which ran one group behind); at the page bottom it is the last group; the active
       chip is scrolled clear of the right-hand fade inside the strip only (the page never moves). */
    const chips = $$('a[href^="#"]', faqNav);
    const groups = chips.map((a) => [a, document.getElementById(decodeURIComponent(a.hash.slice(1)))]).filter(([, g]) => g);
    const reveal = (a) => {
      if (faqNav.scrollWidth <= faqNav.clientWidth + 1) return;
      const pad = 56, l = a.offsetLeft, r = l + a.offsetWidth, sl = faqNav.scrollLeft, w = faqNav.clientWidth;
      let to = sl;
      if (l - 16 < sl) to = l - 16; else if (r + pad > sl + w) to = r + pad - w;
      if (to !== sl) faqNav.scrollTo({ left: Math.max(0, to), behavior: motion ? 'smooth' : 'auto' });
    };
    const mark = (a) => {
      if (a && a.getAttribute('aria-current') === 'true') return;
      chips.forEach((c) => c.removeAttribute('aria-current'));
      if (!a) return;
      a.setAttribute('aria-current', 'true');
      reveal(a);
    };
    let ticking = false, lock = 0;
    const wideFaq = matchMedia('(min-width: 990px)');
    const update = () => {
      ticking = false;
      if (Date.now() < lock || !groups.length) return;
      const line = wideFaq.matches ? innerHeight * 0.3 : faqNav.getBoundingClientRect().bottom + 24;
      const doc = document.documentElement;
      let cur = null;
      if (innerHeight + scrollY >= doc.scrollHeight - 4 && groups[0][1].getBoundingClientRect().top < line) cur = groups[groups.length - 1][0];
      else groups.forEach(([a, g]) => { if (g.getBoundingClientRect().top <= line) cur = a; });
      mark(cur);
    };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    addEventListener('resize', update, { passive: true });
    faqNav.addEventListener('click', (e) => { const a = e.target.closest('a'); if (a) { lock = Date.now() + 900; mark(a); } });
    update();
  }

  /* 10c. Product photos on desktop: a click (or Enter) on a small photo brings it into the large frame, with a
          crossfade; the small frame takes the photo that was large. Phones keep the swipe gallery. */
  $$('.gallery--pdp').forEach((g) => {
    const wide = matchMedia('(min-width: 990px)');
    const frames = () => [...g.children].filter((el) => el.classList.contains('frame'));
    const LEAD = '(min-width: 990px) 52vw, 86vw';
    const THUMB = '(min-width: 990px) 18vw, 86vw';
    const setSizes = (frame, v) => $$('img, source', frame).forEach((el) => { if (el.hasAttribute('sizes')) el.setAttribute('sizes', v); });
    const label = () => frames().forEach((f, i) => {
      if (i === 0 || !wide.matches) { f.removeAttribute('role'); f.removeAttribute('tabindex'); f.removeAttribute('aria-label'); return; }
      const alt = $('img', f)?.alt || 'this photo';
      f.setAttribute('role', 'button'); f.setAttribute('tabindex', '0'); f.setAttribute('aria-label', `Show larger: ${alt}`);
    });
    const swap = (thumb) => {
      const lead = frames()[0];
      if (!lead || thumb === lead) return;
      const a = lead.firstElementChild; const b = thumb.firstElementChild;
      if (!a || !b) return;
      const ph = document.createComment('');
      lead.replaceChild(ph, a); thumb.replaceChild(a, b); lead.replaceChild(b, ph);
      setSizes(lead, LEAD); setSizes(thumb, THUMB);
      $('img', b)?.setAttribute('loading', 'eager');
      if (motion) b.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, easing: 'ease-out' });
      label();
    };
    g.addEventListener('click', (e) => {
      if (!wide.matches) return;
      const f = e.target.closest('.frame');
      if (f && f.parentElement === g && f !== frames()[0]) swap(f);
    });
    g.addEventListener('keydown', (e) => {
      if (!wide.matches || (e.key !== 'Enter' && e.key !== ' ')) return;
      const f = e.target.closest('.frame');
      if (f && f.parentElement === g && f !== frames()[0]) { e.preventDefault(); swap(f); }
    });
    label();
    wide.addEventListener?.('change', label);
  });

  /* 11a. Seasonal campaigns (snippets/campaigns-data.liquid): one banner and one popup at a time. The running entry
          with the highest priority wins (ties: the later start); a pinned entry wins while it runs. Static dates, no
          timers. ?alpaka_now=2026-12-14T23:00:00-10:00 previews another moment for this visitor only. */
  const params = new URLSearchParams(location.search);
  const campaigns = (() => { try { return JSON.parse($('#alpaka-campaigns')?.textContent || 'null'); } catch { return null; } })();
  const nowSec = (() => { const t = Date.parse(params.get('alpaka_now') || ''); return (Number.isFinite(t) ? t : Date.now()) / 1000; })();
  const pick = (kind) => {
    if (!campaigns?.list?.length) return null;
    const live = campaigns.list.filter((c) => c[kind] && c.start <= nowSec && nowSec <= c.end);
    const pinned = live.find((c) => c.handle === campaigns.pinned);
    if (pinned) return pinned;
    return live.sort((a, b) => (b.priority - a.priority) || (b.start - a.start))[0] || null;
  };
  const banner = pick('banner');
  if (banner) {
    const past = banner.cutoff && nowSec > banner.cutoff;
    const text = past ? (banner.after || '') : banner.text;
    const host = $('[data-announce-text]');
    if (host && text) {
      host.textContent = '';
      const inner = banner.link ? Object.assign(document.createElement('a'), { href: banner.link }) : document.createElement('span');
      inner.textContent = text;
      host.append(inner);
    }
    if (banner.pdp && !past) {
      $$('[data-campaign-pdp]').forEach((el) => { $('[data-campaign-pdp-text]', el).textContent = banner.pdp; el.hidden = false; });
    }
  }

  /* 11b. Email letter (FINAL-SITE-SPEC 5.2): question first, then email, in one form post. Desktop: a small window
          from the 2nd page view after 20 s or half the page. Phones: only a small tab, which opens the sheet when
          tapped. Never with utm_medium=email, after adding to cart in this session, or where data-blocked is set.
          Closed: quiet for 21 days (a different campaign may ask again after 14). Subscribed: never again. */
  const letter = $('#Letter');
  const tab = $('[data-letter-tab]');
  if (letter) {
    const KEY = 'alpaka-letter';
    const s = store.get(KEY);
    s.views = (s.views || 0) + 1;
    if (!s.closed) s.closed = {};
    store.set(KEY, s);
    const days = parseInt(letter.dataset.days, 10) || 21;
    const desktop = matchMedia('(min-width: 750px)').matches;
    const popup = pick('popup');
    const handle = popup?.handle || 'next-batch';
    const success = $('[data-letter-success]', letter);
    const hasErrors = $('[data-letter-errors]', letter);
    const tags = $('[data-letter-tags]', letter);
    const soldOut = letter.dataset.soldOut === 'true';
    let seg = '';
    const setText = (sel, val) => { const el = $(sel, letter); if (el && val) el.textContent = val; };
    if (popup) {
      setText('[data-letter-kicker]', popup.kicker);
      setText('[data-letter-heading]', popup.heading);
      setText('[data-letter-body]', popup.body);
      setText('[data-letter-button]', popup.button);
      if (tab && popup.teaser) $('[data-letter-tab-label]', tab).textContent = popup.teaser;
    }
    const writeTags = () => {
      if (!tags) return;
      const list = ['newsletter', popup?.lead || 'lead-next-batch', 'popup'];
      if (seg) list.push(seg);
      if (soldOut) list.push('waitlist:next-batch');
      tags.value = list.join(',');
    };
    if (soldOut) { const plus = $('[data-letter-plus]', letter); if (plus) plus.hidden = false; }
    writeTags();
    const step = (n) => {
      $$('[data-letter-step]', letter).forEach((el) => { el.hidden = el.dataset.letterStep !== String(n); });
      if (n === 2) setTimeout(() => $('input[type="email"]', letter)?.focus({ preventScroll: true }), 40);
    };
    $$('[data-seg]', letter).forEach((b) => b.addEventListener('click', () => { seg = b.dataset.seg; writeTags(); step(2); }));
    $('#LetterForm', letter)?.addEventListener('submit', () => { const v = store.get(KEY); v.pending = handle; store.set(KEY, v); });
    const open = () => {
      if (letter.open) return;
      if (tab) tab.hidden = true;
      letter.showModal();
      try { sessionStorage.setItem('alpaka-letter-shown', '1'); } catch { /* private mode */ }
      setTimeout(() => (step2 ? $('input[type="email"]', letter) : $('[data-seg]', letter))?.focus({ preventScroll: true }), 60);
    };
    let step2 = false;
    const dismiss = () => { const v = store.get(KEY); v.dismissed = Date.now(); v.lastClosedAt = Date.now(); v.closed = Object.assign(v.closed || {}, { [handle]: Date.now() }); store.set(KEY, v); };
    letter.addEventListener('click', (e) => {
      if (e.target === letter || e.target.closest('[data-letter-close]')) { dismiss(); letter.close(); }
    });
    letter.addEventListener('cancel', dismiss);
    let fromTab = false;
    tab?.addEventListener('click', () => { fromTab = true; open(); });
    // closed from the tab: a small seal stays for the rest of this page view, so the guide can still be opened
    letter.addEventListener('close', () => { if (tab && fromTab && !success) { tab.hidden = false; tab.classList.add('is-collapsed'); tab.setAttribute('aria-label', $('[data-letter-tab-label]', tab)?.textContent || 'Free cacao guide'); } });
    if (success) {
      const v = store.get(KEY);
      const done = campaigns?.list?.find((c) => c.handle === v.pending);
      if (done?.success) { const t = $('[data-letter-success-text]', letter); if (t) t.textContent = done.success; }
      v.subscribed = true; delete v.pending; store.set(KEY, v);
      open();
    } else if (hasErrors) {
      step2 = true; step(2); open();
    } else {
      const session = (() => { try { return sessionStorage.getItem('alpaka-letter-shown') === '1'; } catch { return false; } })();
      const added = (() => { try { return sessionStorage.getItem('alpaka-added') === '1'; } catch { return false; } })();
      const closedThis = s.closed?.[handle] && Date.now() - s.closed[handle] < days * 864e5;
      const lastClosed = s.lastClosedAt || s.dismissed; // `dismissed` is the v2 key
      const closedOther = lastClosed && Date.now() - lastClosed < 14 * 864e5;
      const blocked = s.subscribed || closedThis || closedOther || session || added || s.views < 2
        || params.get('utm_medium') === 'email' || letter.dataset.blocked === 'true';
      if (!blocked) {
        let done = false;
        const go = () => {
          if (done) return; done = true; removeEventListener('scroll', onScroll);
          if (desktop && params.get('utm_medium') !== 'paid_social') open(); else if (tab) tab.hidden = false;
        };
        const onScroll = () => {
          const h = document.documentElement.scrollHeight - innerHeight;
          if (h > 0 && scrollY / h >= (parseInt(letter.dataset.scroll, 10) || 50) / 100) go();
        };
        setTimeout(() => {
          setTimeout(go, Math.max(0, (parseInt(letter.dataset.delay, 10) || 20) * 1000 - 10000));
          addEventListener('scroll', onScroll, { passive: true });
        }, 10000);
      }
    }
  }

  /* 12. Group calculator (facilitators page). */
  $$('[data-calc]').forEach((c) => {
    const guests = $('[name="guests"]', c);
    const out = $('[data-guests-out]', c);
    const block = parseInt(c.dataset.block, 10) || 453;
    const water = parseInt(c.dataset.water, 10) || 180;
    const fmt = (g) => (g >= 1000 ? `${(g / 1000).toFixed(2).replace(/\.?0+$/, '')} kg` : `${Math.round(g / 5) * 5} g`);
    const fmtOz = (g) => { const oz = g / 28.35; return oz >= 16 ? `${(oz / 16).toFixed(1).replace(/\.0$/, '')} lb` : `${oz.toFixed(1).replace(/\.0$/, '')} oz`; };
    const update = () => {
      const n = parseInt(guests.value, 10) || 1;
      const size = parseInt($('[name="cup"]:checked', c)?.value, 10) || 25;
      const grams = n * size * 1.1;
      const litres = (n * water) / 1000;
      out.textContent = n;
      $('[data-out-grams]', c).textContent = fmt(grams);
      const oz = $('[data-out-oz]', c); if (oz) oz.textContent = fmtOz(grams);
      const blocks = Math.ceil(grams / block);
      $('[data-out-blocks]', c).textContent = blocks;
      $('[data-out-blocks-label]', c).textContent = blocks === 1 ? '1 lb block' : '1 lb blocks';
      // QA v3-3: litres lead; "9 cups" for 12 guests read as servings, so US cups are the small, explicit aside
      $('[data-out-water]', c).textContent = `${litres.toFixed(litres < 10 ? 1 : 0)} L`;
      $('[data-out-water-cups]', c).textContent = `about ${Math.round(litres * 4.227)} US cups`;
      const cta = $('[data-out-cta]', c);
      if (cta) {
        const url = new URL(cta.href, location.origin);
        url.searchParams.set('blocks', String(blocks));
        cta.href = url.pathname + url.search;
        cta.textContent = blocks > 12 ? 'Write to us for this many' : `Get ${blocks} ${blocks === 1 ? 'block' : 'blocks'}`;
        if (blocks > 12) cta.href = '#inquiry';
      }
    };
    c.addEventListener('input', update);
    update();
  });

  /* 13. Copy the "read aloud" text for facilitators. */
  $$('[data-copyable] blockquote').forEach((q) => {
    if (!navigator.clipboard) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'btn btn--ghost btn--sm copy-btn';
    b.textContent = 'Copy this text';
    b.addEventListener('click', () => navigator.clipboard.writeText(q.innerText.trim()).then(() => { b.textContent = 'Copied'; setTimeout(() => { b.textContent = 'Copy this text'; }, 1600); }));
    q.after(b);
  });

  /* 14. Print a recipe card. */
  $$('[data-print]').forEach((b) => b.addEventListener('click', () => window.print()));
  /* 15. Leaving for checkout (Buy now, Check out): no cross-document view transition, which the checkout cannot
         take part in (it logged "Transition was aborted because of invalid state"). */
  addEventListener('pageswap', (e) => {
    if (!e.viewTransition) return;
    try {
      const to = new URL(e.activation?.entry?.url || '', location.href);
      if (to.origin !== location.origin || /^\/(cart\/|checkouts?)/.test(to.pathname)) e.viewTransition.skipTransition();
    } catch { e.viewTransition.skipTransition(); }
  });

  /* 15b. Tables in page text are named after their heading (each scroll region gets its own name). */
  $$('.table-wrap[aria-label="Table"]').forEach((w) => {
    let el = w; let h = null;
    while (el && !h) { let p = el.previousElementSibling; while (p && !h) { h = p.matches('h2, h3') ? p : p.querySelector?.('h2, h3:last-of-type'); p = p.previousElementSibling; } el = el.parentElement; if (el?.matches('main, body')) break; }
    const name = (h?.textContent || document.title.split('|')[0]).trim();
    w.setAttribute('aria-label', `${name}: table`);
  });

  /* 16. An answer that opens low on the screen scrolls up just enough to show above the sticky buy bar. */
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!(d instanceof HTMLDetailsElement) || !d.open || d.closest('dialog, .buy, .toc, .site-header')) return;
    // measured once the answer has finished opening (it animates its height)
    setTimeout(() => {
      if (!d.open) return;
      const bar = document.body.classList.contains('sticky-buy-on') ? $('[data-sticky-buy]') : null;
      if (!bar) return; // QA round 2: no unasked-for scrolling on desktop; only the phone buy bar can hide an answer
      const floor = bar.getBoundingClientRect().top - 16;
      const r = d.getBoundingClientRect();
      const over = r.bottom - floor;
      if (over > 0) scrollBy({ top: Math.max(0, Math.min(over, r.top - (parseFloat(getComputedStyle(root).scrollPaddingTop) || 80))), behavior: motion ? 'smooth' : 'auto' });
    }, 450);
  }, true);
})();

/* v3 round 1 (QA 2026-10-16) */
(() => {
  if (!('IntersectionObserver' in window)) return;
  /* Cart page on phones: hide the sticky Check out bar while the summary's own Check out button is on screen. */
  const sticky = document.querySelector('.cart-sticky');
  const own = document.querySelector('.cart-summary button[name="checkout"]');
  if (sticky && own) {
    new IntersectionObserver(([e]) => { sticky.classList.toggle('is-off', e.isIntersecting); }).observe(own);
  }
  /* Letter tab: hidden while the footer is on screen (it covered the legal line), and folded into the small seal once
     the reader has scrolled a screen and a half (the open tab sat on top of article text). */
  const tab = document.querySelector('[data-letter-tab]');
  const footer = document.querySelector('.site-footer');
  if (tab && footer) {
    new IntersectionObserver(([e]) => { tab.classList.toggle('is-away', e.isIntersecting); }, { rootMargin: '0px 0px -40px 0px' }).observe(footer);
  }
  if (tab) {
    const fold = () => {
      if (scrollY < innerHeight * 1.5 || tab.classList.contains('is-collapsed')) return;
      tab.classList.add('is-collapsed');
      tab.setAttribute('aria-label', tab.querySelector('[data-letter-tab-label]')?.textContent || 'Free cacao guide');
      removeEventListener('scroll', fold);
    };
    addEventListener('scroll', fold, { passive: true });
  }
  /* QA v3-6: a drop cap never hangs below an intro shorter than three lines (it shrinks to two lines). */
  const capFit = () => document.querySelectorAll('.dropcap > p:first-of-type').forEach((el) => {
    el.classList.remove('is-short');
    const r = document.createRange(); r.selectNodeContents(el);
    const t0 = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.textContent.trim() ? 1 : 3) }).nextNode();
    if (t0) r.setStart(t0, Math.min(t0.textContent.length, t0.textContent.search(/\S/) + 1)); // leave the cap letter out
    const lh = parseFloat(getComputedStyle(el).lineHeight) || 26; // the cap's own tall box is left out
    const tops = [...r.getClientRects()].filter((q) => q.width > 2 && q.height < lh * 1.5).map((q) => q.top).sort((a, b) => a - b);
    const lines = tops.reduce((n, t, i) => n + (i === 0 || t - tops[i - 1] > lh * 0.5 ? 1 : 0), 0);
    if (lines && lines < 3) el.classList.add('is-short');
  });
  document.fonts?.ready.then(capFit);
  let capT; addEventListener('resize', () => { clearTimeout(capT); capT = setTimeout(capFit, 200); });
})();
